import type { MaintenanceItemDoc, MaintenanceTrack } from './types';

// NOTE ON METERS: all maintenance hours-tracking is measured against the
// aircraft TACH (engine time), never Hobbs. Hobbs is billing-only. The
// `dueHobbs`/`lastDoneHobbs` fields are legacy names that hold TACH readings;
// always pass `aircraft.currentTach` here, not `currentHobbs`.

// 0 ok · 1 due-soon · 2 due-now · 3 overdue. Thresholds: 30/7 days, 25/10 hrs.
export type MxLevel = 0 | 1 | 2 | 3;
export const MX_LABEL: Record<MxLevel, string> = { 0: 'OK', 1: 'Due soon', 2: 'Due now', 3: 'Overdue' };
export const MX_TONE: Record<MxLevel, 'ok' | 'warning' | 'danger'> = {
  0: 'ok',
  1: 'warning',
  2: 'danger',
  3: 'danger',
};

// A knowingly-postponed item (e.g. oil folded into an upcoming annual). Plain
// YYYY-MM-DD string compare, no Date parsing — functions/index.js carries an
// identical copy of this predicate and the two must not drift across timezones.
export function isDeferred(item: MaintenanceItemDoc, ref: Date = new Date()): boolean {
  if (!item.deferredUntil) return false;
  const today = new Date(ref.getTime() - ref.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  return item.deferredUntil >= today;
}

function levelFromDays(d: number): MxLevel {
  return d < 0 ? 3 : d <= 7 ? 2 : d <= 30 ? 1 : 0;
}
function levelFromHours(h: number): MxLevel {
  return h < 0 ? 3 : h <= 10 ? 2 : h <= 25 ? 1 : 0;
}

export function mxStatus(item: MaintenanceItemDoc, currentTach: number, ref: Date = new Date()) {
  let daysRemaining: number | null = null;
  let hoursRemaining: number | null = null;
  let level: MxLevel = 0;
  if ((item.track === 'date' || item.track === 'both') && item.dueDate) {
    const due = new Date(`${item.dueDate}T12:00:00`);
    daysRemaining = Math.round((due.getTime() - ref.getTime()) / 86400000);
    const l = levelFromDays(daysRemaining);
    if (l > level) level = l;
  }
  if ((item.track === 'hours' || item.track === 'both') && typeof item.dueHobbs === 'number') {
    hoursRemaining = +(item.dueHobbs - currentTach).toFixed(1);
    const l = levelFromHours(hoursRemaining);
    if (l > level) level = l;
  }
  // `level` is deliberately NOT clamped for deferred items: Preflight/LogFlight
  // read it to ground the aircraft on overdue airworthiness items. Call sites that
  // are cosmetic (badges, chips) check `deferred` themselves.
  return { level, daysRemaining, hoursRemaining, deferred: isDeferred(item, ref) };
}

// The hours-tracked item closest to coming due (smallest hours remaining,
// most-overdue first). Used for the "hrs until maintenance" chip. Deferred items
// are skipped — the chip answers "what's coming up for you, pilot", and a
// knowingly-postponed item isn't that.
export function nextDueByHours(items: MaintenanceItemDoc[], currentTach: number) {
  const candidates = items
    .filter((m) => !isDeferred(m) && (m.track === 'hours' || m.track === 'both') && typeof m.dueHobbs === 'number')
    .map((m) => ({ item: m, hoursRemaining: +((m.dueHobbs as number) - currentTach).toFixed(1) }));
  if (!candidates.length) return null;
  candidates.sort((a, b) => a.hoursRemaining - b.hoursRemaining);
  return candidates[0];
}

// The date-tracked item closest to coming due (fewest days remaining first).
export function nextDueByDate(items: MaintenanceItemDoc[], ref: Date = new Date()) {
  const candidates = items
    .filter((m) => !isDeferred(m, ref) && (m.track === 'date' || m.track === 'both') && m.dueDate)
    .map((m) => ({
      item: m,
      daysRemaining: Math.round((new Date(`${m.dueDate}T12:00:00`).getTime() - ref.getTime()) / 86400000),
    }));
  if (!candidates.length) return null;
  candidates.sort((a, b) => a.daysRemaining - b.daysRemaining);
  return candidates[0];
}

export function mxSummary(item: MaintenanceItemDoc, currentTach: number, ref: Date = new Date()): string {
  const { daysRemaining, hoursRemaining } = mxStatus(item, currentTach, ref);
  const parts: string[] = [];
  if (daysRemaining != null)
    parts.push(daysRemaining < 0 ? `${Math.abs(daysRemaining)}d overdue` : `${daysRemaining}d`);
  if (hoursRemaining != null)
    parts.push(hoursRemaining < 0 ? `${Math.abs(hoursRemaining).toFixed(1)} hr over` : `${hoursRemaining.toFixed(1)} hr`);
  return parts.join(' · ') || '—';
}

// Common Cessna 172 items, pre-filled for quick-add (owner sets the due values).
export const MX_PRESETS: Array<{
  label: string;
  track: MaintenanceTrack;
  intervalDays?: number;
  intervalHours?: number;
  isAirworthiness: boolean;
}> = [
  { label: 'Annual Inspection', track: 'date', intervalDays: 365, isAirworthiness: true },
  { label: '100-Hour Inspection', track: 'hours', intervalHours: 100, isAirworthiness: true },
  { label: 'Oil Change', track: 'hours', intervalHours: 100, isAirworthiness: false },
  { label: 'Transponder / Static (91.411 & .413)', track: 'date', intervalDays: 730, isAirworthiness: true },
  { label: 'ELT Battery', track: 'date', intervalDays: 365, isAirworthiness: false },
  { label: 'Recurring AD', track: 'both', isAirworthiness: true },
];

/**
 * The aircraft's `annualDueDate` is what bookings, preflight and the dashboard
 * read; the Annual tracker item is where the owner records the inspection.
 * When the Annual item gets a new due date (Mark complete, an edit, or a
 * logbook reading), the aircraft's date has to move with it. Returns that new
 * date, or null when this isn't the Annual item or it has no due date.
 */
export function annualDueFrom(item: { label?: string }, dueDate?: string | null): string | null {
  return dueDate && /\bannual\b/i.test(item.label ?? '') ? dueDate : null;
}
